import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  DollarSign, TrendingUp, Calendar, ChevronRight, Star,
  Trophy, Flame, Target, Clock, Gift, Download
} from 'lucide-react';
import { MobileStatsCard, MobileCard, MobileProgress, MobileBadge } from '../components/MobileUIKit';
import { MobileBottomNav } from '../components/MobileBottomNav';
import '../styles/mobile.css';

// Mock earnings data
const mockEarnings = {
  today: 127.50,
  todayTrend: '+15%',
  week: 847.50,
  weekTrend: '+12%',
  month: 3250.00,
  
  breakdown: {
    base: 625.00,
    tips: 142.50,
    bonuses: 80.00,
  },
  
  dailyData: [
    { day: 'Mon', amount: 156.00, deliveries: 14 },
    { day: 'Tue', amount: 134.50, deliveries: 12 },
    { day: 'Wed', amount: 178.00, deliveries: 16 },
    { day: 'Thu', amount: 98.50, deliveries: 9 },
    { day: 'Fri', amount: 195.50, deliveries: 18 },
    { day: 'Sat', amount: 85.00, deliveries: 8 },
    { day: 'Sun', amount: 0, deliveries: 0, isToday: true },
  ],
  
  recentDeliveries: [
    { id: 'ORD-4521', time: '2:45 PM', merchant: 'Burger Palace', address: 'Oak Ave', amount: 12.50, tip: 2.50 },
    { id: 'ORD-4520', time: '1:30 PM', merchant: 'Pizza Hut', address: 'Main St', amount: 8.75, tip: 1.75 },
    { id: 'ORD-4519', time: '12:15 PM', merchant: 'Taco Bell', address: 'Elm St', amount: 14.25, tip: 4.00 },
    { id: 'ORD-4518', time: '11:00 AM', merchant: 'Starbucks', address: 'Pine Rd', amount: 6.50, tip: 1.00 },
  ],
  
  achievements: [
    { id: 1, name: 'Perfect Week', icon: '⭐', description: '5.0 rating all week', earned: true },
    { id: 2, name: '10 Streak', icon: '🔥', description: '10 deliveries in a row', earned: true },
    { id: 3, name: 'VIP Driver', icon: '💎', description: 'Top 10% this month', earned: true },
    { id: 4, name: 'Speed Demon', icon: '⚡', description: '50 early deliveries', earned: false },
  ],
};

export const DriverEarningsPage = () => {
  const navigate = useNavigate();
  const [timePeriod, setTimePeriod] = useState('week');

  const maxDailyAmount = Math.max(...mockEarnings.dailyData.map(d => d.amount));

  return (
    <div className="mobile-app min-h-screen pb-20">
      {/* Header */}
      <div className="mobile-safe-top mobile-px-4 pt-4 pb-3">
        <div className="mobile-flex mobile-justify-between mobile-items-center">
          <h1 className="mobile-text-title" style={{ color: 'var(--mobile-text-primary)' }}>
            Earnings
          </h1>
          <div 
            className="mobile-flex mobile-gap-1 p-1 rounded-lg"
            style={{ background: 'var(--mobile-bg-secondary)' }}
          >
            {['day', 'week', 'month'].map(period => (
              <button
                key={period}
                onClick={() => setTimePeriod(period)}
                className="px-3 py-1 rounded-md text-xs font-medium capitalize"
                style={{
                  background: timePeriod === period ? 'var(--ezp-primary)' : 'transparent',
                  color: timePeriod === period ? 'white' : 'var(--mobile-text-secondary)',
                }}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Hero Earnings Card */}
      <div className="mobile-px-4 mb-4">
        <MobileCard elevated className="text-center py-6">
          <DollarSign 
            className="w-8 h-8 mx-auto mb-2" 
            style={{ color: 'var(--mobile-earnings)' }}
          />
          <p 
            className="mobile-earnings-hero"
            style={{ 
              color: 'var(--mobile-earnings)',
              textShadow: '0 0 30px rgba(0, 200, 83, 0.4)'
            }}
          >
            ${timePeriod === 'day' 
              ? (mockEarnings.today ?? 0).toFixed(2)
              : timePeriod === 'week'
              ? (mockEarnings.week ?? 0).toFixed(2)
              : (mockEarnings.month ?? 0).toFixed(2)
            }
          </p>
          <p className="mobile-text-caption mt-1">
            {timePeriod === 'day' ? "Today's" : timePeriod === 'week' ? 'This Week' : 'This Month'} Earnings
          </p>
          <div 
            className="mobile-flex mobile-items-center mobile-justify-center mobile-gap-1 mt-2"
            style={{ color: 'var(--mobile-success)' }}
          >
            <TrendingUp className="w-4 h-4" />
            <span className="text-sm font-medium">
              {timePeriod === 'week' ? mockEarnings.weekTrend : mockEarnings.todayTrend} vs last {timePeriod}
            </span>
          </div>
        </MobileCard>
      </div>

      {/* Earnings Breakdown */}
      <div className="mobile-px-4 mb-4">
        <div className="grid grid-cols-3 gap-3">
          <MobileStatsCard
            value={`$${(mockEarnings.breakdown?.base ?? 0).toFixed(0)}`}
            label="Base Pay"
            color="default"
          />
          <MobileStatsCard
            value={`$${(mockEarnings.breakdown?.tips ?? 0).toFixed(0)}`}
            label="Tips"
            color="earnings"
          />
          <MobileStatsCard
            value={`$${(mockEarnings.breakdown?.bonuses ?? 0).toFixed(0)}`}
            label="Bonuses"
            color="warning"
          />
        </div>
      </div>

      {/* Daily Breakdown Chart */}
      <div className="mobile-px-4 mb-4">
        <MobileCard>
          <h3 
            className="mobile-text-subtitle mb-4"
            style={{ color: 'var(--mobile-text-primary)' }}
          >
            📊 Daily Breakdown
          </h3>
          <div className="mobile-flex mobile-items-end mobile-justify-between h-32">
            {mockEarnings.dailyData.map((day, index) => (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div 
                  className="w-full max-w-[30px] rounded-t-md transition-all"
                  style={{
                    height: day.amount > 0 ? `${(day.amount / maxDailyAmount) * 100}%` : '4px',
                    background: day.isToday 
                      ? 'var(--mobile-text-disabled)' 
                      : 'var(--ezp-primary)',
                    minHeight: '4px',
                  }}
                />
                <p className="mobile-text-tiny mt-2">{day.day}</p>
                <p 
                  className="text-[10px] font-semibold"
                  style={{ color: day.isToday ? 'var(--mobile-text-disabled)' : 'var(--mobile-text-primary)' }}
                >
                  {day.isToday ? 'Today' : `$${day.amount}`}
                </p>
              </div>
            ))}
          </div>
        </MobileCard>
      </div>

      {/* Achievements */}
      <div className="mobile-px-4 mb-4">
        <h3 
          className="mobile-text-subtitle mb-3"
          style={{ color: 'var(--mobile-text-primary)' }}
        >
          🏆 Achievements This Week
        </h3>
        <div className="mobile-flex mobile-gap-4 overflow-x-auto pb-2">
          {mockEarnings.achievements.filter(a => a.earned).map(achievement => (
            <div 
              key={achievement.id}
              className="flex flex-col items-center min-w-[80px]"
            >
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                style={{ 
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  boxShadow: '0 4px 15px rgba(255, 215, 0, 0.3)'
                }}
              >
                {achievement.icon}
              </div>
              <p 
                className="mobile-text-tiny mt-2 text-center font-medium"
                style={{ color: 'var(--mobile-text-primary)' }}
              >
                {achievement.name}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Deliveries */}
      <div className="mobile-px-4 mb-4">
        <div className="mobile-flex mobile-justify-between mobile-items-center mb-3">
          <h3 
            className="mobile-text-subtitle"
            style={{ color: 'var(--mobile-text-primary)' }}
          >
            📋 Recent Deliveries
          </h3>
          <button 
            className="mobile-text-caption"
            style={{ color: 'var(--ezp-primary)' }}
          >
            See All
          </button>
        </div>
        
        {mockEarnings.recentDeliveries.map((delivery, index) => (
          <MobileCard key={delivery.id} className="mb-2">
            <div className="mobile-flex mobile-justify-between mobile-items-center">
              <div className="mobile-flex mobile-items-center mobile-gap-3">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ background: 'var(--mobile-success-muted)' }}
                >
                  ✓
                </div>
                <div>
                  <p 
                    className="font-medium text-sm"
                    style={{ color: 'var(--mobile-text-primary)' }}
                  >
                    {delivery.merchant} → {delivery.address}
                  </p>
                  <p className="mobile-text-tiny">Today, {delivery.time}</p>
                </div>
              </div>
              <div className="text-right">
                <p 
                  className="font-bold"
                  style={{ color: 'var(--mobile-earnings)' }}
                >
                  ${(delivery.amount ?? 0).toFixed(2)}
                </p>
                {delivery.tip > 0 && (
                  <p 
                    className="mobile-text-tiny"
                    style={{ color: 'var(--mobile-tip)' }}
                  >
                    +${(delivery.tip ?? 0).toFixed(2)} tip
                  </p>
                )}
              </div>
            </div>
          </MobileCard>
        ))}
      </div>

      {/* Cash Out Button */}
      <div className="mobile-px-4 mb-4">
        <MobileCard 
          elevated 
          interactive
          className="mobile-flex mobile-items-center mobile-justify-between"
        >
          <div className="mobile-flex mobile-items-center mobile-gap-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{ background: 'var(--mobile-earnings-glow)' }}
            >
              <Download className="w-5 h-5" style={{ color: 'var(--mobile-earnings)' }} />
            </div>
            <div>
              <p 
                className="font-medium"
                style={{ color: 'var(--mobile-text-primary)' }}
              >
                Instant Cash Out
              </p>
              <p className="mobile-text-tiny">Available: ${(mockEarnings.week ?? 0).toFixed(2)}</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5" style={{ color: 'var(--mobile-text-tertiary)' }} />
        </MobileCard>
      </div>

      {/* Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default DriverEarningsPage;
